import { z } from 'zod'
import { type CreateOrderRequest } from 'ecosystem'

/**
 * Validates the checkout's POST body.
 *
 * Deliberately not a mirror of `CreateOrderRequest` — the cart lines carry the
 * whole product DTO and restating it here would be a second source of truth
 * that silently rots. What is checked is what this flow dereferences before
 * anything else can: without these, the failure lands somewhere deep inside an
 * order-line factory as a `TypeError` and surfaces as an opaque 400. The rest
 * is carried through as-is and still typed by `CreateOrderRequest`.
 */
const createOrderRequestSchema = z.object({
  storeId: z.string().min(1),
  items: z
    .array(
      z.looseObject({
        product: z.looseObject({ id: z.string().min(1) }),
        quantity: z.number()
      })
    )
    .min(1),
  cartDiscountCode: z.looseObject({}).nullish(),
  cartStateUuid: z.string().nullish(),
  countryCode: z.string().optional()
})

export interface ParsedCreateOrderRequest {
  payload?: CreateOrderRequest
  error?: string
}

/**
 * `request.json()` throws on a malformed body and resolves to `any` on a
 * well-formed one, so both failure modes are funnelled through here and come
 * back as a message the caller can answer with.
 */
export const parseCreateOrderRequest = async (
  request: Request
): Promise<ParsedCreateOrderRequest> => {
  let body: unknown

  try {
    body = await request.json()
  } catch {
    return { error: 'Request body is not valid JSON.' }
  }

  const result = createOrderRequestSchema.safeParse(body)

  if (!result.success) {
    const [issue] = result.error.issues

    return {
      error: issue
        ? `Invalid create-order request: ${issue.path.join('.') || 'body'} — ${issue.message}`
        : 'Invalid create-order request.'
    }
  }

  return { payload: body as CreateOrderRequest }
}
